'use client';

import { useEffect, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import {
  Badge,
  Button,
  Field,
  Input,
  Modal,
  Select,
  Spinner,
  Table,
  Textarea,
  type Column,
} from '@/ui';
import { fmtDateTime } from '@/lib/billing-ui';
import {
  portalChangeRequestMessagesApi,
  portalChangeRequestsApi,
  type PortalChangeRequest,
  type PortalDeal,
} from '@/lib/api/portal';
import {
  CHANGE_REQUEST_STATUS_LABELS,
  CHANGE_REQUEST_STATUS_TONES,
  ChangeRequestThreadModal,
  useChangeRequestThread,
} from '@/components/change-request-thread';
import { usePortalSession } from './portal-shell';

const COLUMNS: Column<PortalChangeRequest>[] = [
  {
    key: 'subject',
    header: 'Request',
    render: (r) => (
      <div>
        <div className="text-zinc-100">{r.subject}</div>
        <div className="line-clamp-1 text-xs text-zinc-400">{r.description}</div>
      </div>
    ),
  },
  {
    key: 'created',
    header: 'Sent',
    render: (r) => <span className="tabular-nums">{fmtDateTime(r.createdAt)}</span>,
    noWrap: true,
  },
  {
    key: 'status',
    header: 'Status',
    render: (r) => (
      <Badge tone={CHANGE_REQUEST_STATUS_TONES[r.status] ?? 'neutral'}>{CHANGE_REQUEST_STATUS_LABELS[r.status] ?? r.status}</Badge>
    ),
    noWrap: true,
  },
];

// Change requests as a dashboard section: list + new-request modal + threads.
// Deals come from the dashboard's overview fetch (for the product picker).
export function PortalRequestsSection({ deals }: { deals: PortalDeal[] }) {
  const [requests, setRequests] = useState<PortalChangeRequest[] | null>(null);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [dealId, setDealId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const { brandName } = usePortalSession();
  const { openRequest, messages, openThread, closeThread, sendMessage } = useChangeRequestThread(
    portalChangeRequestMessagesApi,
    (message) => ({
      id: message.id,
      body: message.body,
      createdAt: message.createdAt,
      authorLabel: message.authorType === 'customer' ? 'You' : brandName,
      isOwn: message.authorType === 'customer',
    })
  );

  useEffect(() => {
    portalChangeRequestsApi
      .list()
      .then(setRequests)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Failed to load requests'));
  }, []);

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setFormError('');
    setSubmitting(true);
    try {
      const created = await portalChangeRequestsApi.create({
        subject: subject.trim(),
        description: description.trim(),
        dealId: dealId || null,
      });
      setRequests((prev) => [created, ...(prev ?? [])]);
      setShowForm(false);
      setSubject('');
      setDescription('');
      setDealId('');
      toast.success('Request sent');
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Could not send the request');
    }
    setSubmitting(false);
  }

  if (error) return <p className="text-danger text-sm">{error}</p>;
  if (!requests) return <Spinner />;

  return (
    <div className="grid gap-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-zinc-400">
          Ask for changes to your website or products — our team picks these up and you follow the
          status here.
        </p>
        <Button variant="primary" onClick={() => setShowForm(true)}>
          New request
        </Button>
      </div>
      <Table
        columns={COLUMNS}
        rows={requests}
        rowKey={(r) => r.id}
        onRowClick={openThread}
        empty="No requests yet."
      />

      <Modal open={showForm} title="New change request" onClose={() => setShowForm(false)}>
          <form className="grid gap-4" onSubmit={onSubmit}>
            {deals.length > 0 ? (
              <Field label="Product">
                <Select value={dealId} onChange={(event) => setDealId(event.target.value)}>
                  <option value="">General</option>
                  {deals.map((deal) => (
                    <option key={deal.id} value={deal.id}>
                      {deal.productName ?? 'Product'}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : null}
            <Field label="Subject">
              <Input
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                maxLength={120}
                autoFocus
                required
              />
            </Field>
            <Field label="What should change?" error={formError || undefined}>
              <Textarea
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                rows={6}
                maxLength={5000}
                required
              />
            </Field>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" type="button" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" loading={submitting}>
                Send request
              </Button>
            </div>
          </form>
      </Modal>

      <ChangeRequestThreadModal
        request={openRequest}
        title={openRequest?.subject ?? ''}
        messages={messages}
        loading={<Spinner />}
        onSend={sendMessage}
        onClose={closeThread}
      />
    </div>
  );
}
